import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type StageType =
  | 'assessment'
  | 'preparation'
  | 'issuance'
  | 'post-issuance';

export interface StageTransition {
  id: string;
  from: StageType;
  to: StageType;
  timestamp: Date;
  trigger: 'manual' | 'auto' | 'score_based';
  data?: any;
  achievements?: string[];
  nextSteps?: string[];
}

interface StageState {
  currentStage: StageType;
  previousStage: StageType | null;
  stageHistory: StageTransition[];
  pendingTransition: StageTransition | null;
  stageProgress: Record<StageType, number>;
  isTransitioning: boolean;
  transitionError: string | null;
}

const initialState: StageState = {
  currentStage: 'assessment',
  previousStage: null,
  stageHistory: [],
  pendingTransition: null,
  stageProgress: {
    assessment: 0,
    preparation: 0,
    issuance: 0,
    'post-issuance': 0,
  },
  isTransitioning: false,
  transitionError: null,
};

const stageSlice = createSlice({
  name: 'stage',
  initialState,
  reducers: {
    initiateTransition: (
      state,
      action: PayloadAction<Omit<StageTransition, 'id' | 'timestamp'>>
    ) => {
      const transition: StageTransition = {
        ...action.payload,
        id: `transition_${Date.now()}`,
        timestamp: new Date(),
      };

      state.pendingTransition = transition;
      state.isTransitioning = true;
      state.transitionError = null;
    },
    completeTransition: (state, action: PayloadAction<StageTransition>) => {
      const transition = action.payload;

      state.previousStage = state.currentStage;
      state.currentStage = transition.to;
      state.stageHistory.push(transition);
      state.pendingTransition = null;
      state.isTransitioning = false;
      state.transitionError = null;
    },
    failTransition: (
      state,
      action: PayloadAction<{ transition: StageTransition; error: string }>
    ) => {
      state.pendingTransition = null;
      state.isTransitioning = false;
      state.transitionError = action.payload.error;
    },
    updateStageProgress: (
      state,
      action: PayloadAction<{ stage: StageType; progress: number }>
    ) => {
      state.stageProgress[action.payload.stage] = action.payload.progress;
    },
    setCurrentStage: (state, action: PayloadAction<StageType>) => {
      state.currentStage = action.payload;
    },
    clearTransitionError: state => {
      state.transitionError = null;
    },
  },
});

export const {
  initiateTransition,
  completeTransition,
  failTransition,
  updateStageProgress,
  setCurrentStage,
  clearTransitionError,
} = stageSlice.actions;

export default stageSlice.reducer;
